import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { User } from '@feathr/blackbox';
import { ArchiveModalV1, ContextMenu, toast } from '@feathr/components';
import { useAccount, useUser } from '@feathr/extender/state';
import { getIconForAction, useToggle } from '@feathr/hooks';

interface IOptionsColumnProps {
  readonly user: User;
}

function UserOptions({ user }: IOptionsColumnProps): JSX.Element {
  const account = useAccount();
  const [isArchiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const { t } = useTranslation();
  const currentUser = useUser();
  const email = user.get('email');
  const isCurrentUser = user.id === currentUser.id;

  // TODO: Move this to blackbox #3248
  async function removeUser(): Promise<void> {
    const currentACL = account.get('user_roles');
    try {
      account.set({ user_roles: currentACL.filter((item) => item.user !== user.id) });
      await account.patchDirty();
      user.collection?.remove(user.id);
      toast(t('Removed user.'), { type: 'success' });
    } catch (error) {
      toast(t("Couldn't remove user. {{error}}", { error }), {
        type: 'error',
      });
    }
    toggleArchiveModalOpen();
  }

  async function handleSendResetLink(): Promise<void> {
    try {
      await user.sendPasswordResetLink();
      toast(t('Sent password reset link to {{email}}.', { email: email }), {
        type: 'success',
      });
    } catch (error) {
      toast(t("Couldn't send password reset link. {{error}}", { error }), {
        type: 'error',
      });
    }
  }

  async function handleResendInvite(): Promise<void> {
    try {
      await user.resendInvite();
      toast(t('Sent invite to {{email}}.', { email: email }), {
        type: 'success',
      });
    } catch (error) {
      toast(t("Couldn't resend invite. {{error}}", { error }), {
        type: 'error',
      });
    }
  }

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        {user.get('confirmed') ? (
          <ContextMenu.Item
            disabled={isCurrentUser}
            onClick={handleSendResetLink}
            prefix={getIconForAction('send')}
          >
            {t('Send password reset link')}
          </ContextMenu.Item>
        ) : (
          <ContextMenu.Item
            disabled={isCurrentUser}
            onClick={handleResendInvite}
            prefix={getIconForAction('resend')}
          >
            {t('Resend invite link')}
          </ContextMenu.Item>
        )}
        <ContextMenu.Item
          disabled={isCurrentUser}
          id={`${user.id}-archive`}
          onClick={toggleArchiveModalOpen}
          prefix={getIconForAction('remove')}
          theme={'danger'}
        >
          {t('Remove')}
        </ContextMenu.Item>
      </ContextMenu>
      {isArchiveModalOpen && (
        <ArchiveModalV1
          confirmButtonText={t('Remove user')}
          message={t('Are you sure you want to remove this user from this account?')}
          model={user}
          onClose={toggleArchiveModalOpen}
          onConfirm={removeUser}
          t={t}
          title={t('Remove User')}
        />
      )}
    </>
  );
}

export default observer(UserOptions);
